import ContentItem from '../api/content/ContentItem';
import attachVideoElement, {
  AttachableElement
} from '../common/Reporter/attachVideoElement';
import { DeliveryMode, SourceType } from '../common/constants';
import { getSourceTypeForMime } from '../common/utils/sourceTypeUtils';
import {
  InterceptBinder,
  BoolFunction,
  BindOptionsFunction
} from './utils/InterceptBinder';

const { ECDN10, ECDN11, WEBRTC } = DeliveryMode;
export type CleanupMethod = () => void;

export default abstract class BaseInteraction<T> {
  allowedModes: DeliveryMode[];
  binder: InterceptBinder;
  cleanupMethods: CleanupMethod[];
  element?: HTMLVideoElement;
  player: T;

  constructor(player: T, allowedModes?: DeliveryMode[]) {
    this.player = player;
    this.allowedModes = allowedModes || [WEBRTC, ECDN10, ECDN11];
    this.binder = new InterceptBinder();
    this.cleanupMethods = [];
  }

  /**
   * Binds an interceptor to the player.
   * @param shouldIntercept - a function that returns true if kollective intercept should happen
   * @param getOptions - a function that returns the options necessary for KollectiveXHR
   */
  bindInterceptor(
    shouldIntercept: BoolFunction,
    getOptions: BindOptionsFunction
  ): void {
    this.binder.updateBinder(shouldIntercept, getOptions);
  }

  // Clear source for SRV-712.
  abstract clearSource(): void;
  /**
   * Internal error message generator, for unfinished and/or not yet implemented features.
   */
  generatePlannedErrorMessage(method: string, integration: string): string {
    return `${method} for ${integration} has yet to be implemented, but is planned. Feel free to reach out to Kollective regarding this issue.`;
  }

  /**
   * 10 agent playback specific. This communicates to the 10 agent that it needs to switch to cdn playback.
   * @param items - list of content items
   */
  switchToCDN(items: ContentItem[]): ContentItem[] {
    return this.loader(items);
  }

  /**
   *
   * @param items - content items to set on player
   */
  fallbackToCDN(items: ContentItem[]): ContentItem[] {
    return this.loader(items);
  }

  /**
   *
   * @param items - content items to set on player
   */
  playKollective(items: ContentItem[]): ContentItem[] | undefined {
    const filtered = this.getAllowedSources(items);
    if (filtered.length > 0) {
      return this.loader(filtered, true);
    } else {
      throw Error('No available allowed sources');
    }
  }

  addEventListeners(): void {
    const element = this.videoElement();
    if (element) {
      this.cleanupMethods.push(
        attachVideoElement(element as AttachableElement)
      );
    }
  }

  getPlayer(): T {
    return this.player;
  }

  setVideoElement(element: HTMLVideoElement): void {
    this.element = element;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  abstract loader(items: ContentItem[], isFiltered?: boolean): ContentItem[];

  abstract videoElement(): HTMLVideoElement | undefined;

  /**
   * @returns an array of allowed source types for the implementing interaction class.
   */
  abstract getAllowedSourceTypes(): SourceType[];

  getAllowedSources(items: ContentItem[]): ContentItem[] {
    return items.filter(i => {
      const sourceType = getSourceTypeForMime(i.data.mime, i.url);
      return this.getAllowedSourceTypes().includes(sourceType as SourceType);
    });
  }

  cleanup(): void {
    this.cleanupMethods.forEach(method => method());
    this.binder.reset();
  }
}
